import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent } from "react";
import { ArrowUpRight, Circle, Loader2, MessageSquare, MousePointer2, PaintBucket, Pencil, Redo2, Square, Trash2, Type, Undo2, X } from "lucide-react";
import { Dialog, Modal, ModalOverlay } from "react-aria-components";
import type { ManualMediaRef } from "@arc/contracts";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  drawAnnotation,
  drawSelectionRing,
  fontSizeFor,
  hitTest,
  newId,
  normRect,
  PALETTE,
  strokeWidthFor,
  translateAnnotation,
  type Annotation,
  type Point,
  type Tool,
} from "./annotations.js";
import { boundedForExport } from "../image-export.js";

/** Keys, not labels — this list is module-level and cannot call a hook. */
const TOOLS = [
  { tool: "select", labelKey: "manualEditor.toolSelect", icon: MousePointer2 },
  { tool: "rect", labelKey: "manualEditor.toolRect", icon: Square },
  { tool: "ellipse", labelKey: "manualEditor.toolEllipse", icon: Circle },
  { tool: "arrow", labelKey: "manualEditor.toolArrow", icon: ArrowUpRight },
  { tool: "freehand", labelKey: "manualEditor.toolFreehand", icon: Pencil },
  { tool: "text", labelKey: "manualEditor.toolText", icon: Type },
  { tool: "callout", labelKey: "manualEditor.toolCallout", icon: MessageSquare },
] as const satisfies ReadonlyArray<{ tool: Tool; labelKey: string; icon: typeof Square }>;

/** Grab tolerance (CSS px, converted to natural px via the display scale) for the Select tool, and the drag distance (CSS px)
 *  a pointer must travel before a select-click counts as a MOVE — so a plain click-to-select can't create a 0px undo entry. */
const HIT_SLOP_CSS = 6;
const DRAG_THRESHOLD_CSS = 3;

/**
 * In-house image annotation editor (ADR-025 A3a) — legacy `markerjs2` parity (rectangle · ellipse · arrow · freehand · text ·
 * callout; red/yellow/blue/white palette) on a plain `<canvas>`, no third-party lib/watermark. Loads the step image
 * `crossOrigin` (canvas export taints otherwise), draws in NATURAL pixels, and on Save composites → a PNG (`<name>_edited.png`)
 * → hands it to the caller's `onSave`. That handler lands the edited image straight onto the step (the A3a edit-attach write)
 * and returns the updated body; on success we call `onSaved` (the caller drops back to the lightbox showing the edited image —
 * the author can then close it to return to the manual, or re-open the editor). Cancel / X / dismiss call `onClose`.
 */
export function ImageAnnotateEditor({
  media,
  onClose,
  onSaved,
  onSave,
}: {
  media: ManualMediaRef;
  /** Dismiss without saving (Cancel / X / backdrop) — returns to wherever the editor was opened from. */
  onClose: () => void;
  /** A successful save landed — the caller shows the edited result (the lightbox) rather than the bare manual. */
  onSaved: () => void;
  onSave: (file: File) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [imageReady, setImageReady] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const [tool, setTool] = useState<Tool>("rect");
  const [color, setColor] = useState<string>(PALETTE[0]);
  // Whether a NEW rectangle/ellipse is drawn with a translucent colour fill (a highlighted region) vs an outline only.
  const [fill, setFill] = useState(false);
  const [shapes, setShapes] = useState<Annotation[]>([]);
  const [past, setPast] = useState<Annotation[][]>([]);
  const [future, setFuture] = useState<Annotation[][]>([]);
  const draft = useRef<Annotation | null>(null);
  const drawing = useRef(false);
  // The IMMUTABLE drag anchor for rect/ellipse. The live box is always normRect(anchor, pointer); we must NOT recompute it
  // from the draft's own x/y (normRect returns the min-corner, so a left/up drag would migrate the anchor toward the cursor
  // every frame and shrink the box — 3 of 4 drag directions produced wrong-size shapes, often silently discarded as "tiny").
  const anchor = useRef<Point | null>(null);
  // Pending text/callout input: screen position for the textarea + image coords for the committed shape.
  const [textInput, setTextInput] = useState<{ screenX: number; screenY: number; imgX: number; imgY: number; kind: "text" | "callout" } | null>(null);
  const [textValue, setTextValue] = useState("");
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  // Select tool: the currently-selected shape (drives the dashed ring + keyboard delete/deselect) and the in-flight move
  // gesture. `moveDrag` lives in a ref so a drag mutates NOTHING in React state until pointer-up — the live preview is drawn
  // imperatively (`ctx.translate` in redraw), keeping the drag at 60fps and producing exactly one undo entry per move.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const moveDrag = useRef<{
    id: string;
    original: Annotation;
    before: Annotation[];
    startImgX: number;
    startImgY: number;
    startClientX: number;
    startClientY: number;
    dx: number;
    dy: number;
    moved: boolean;
    pointerId: number;
  } | null>(null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load the source for editing WITHOUT tainting the export canvas. The lightbox already showed this URL in a plain <img>
  // (no crossOrigin) → the browser cached an OPAQUE response, and a crossOrigin <img> for the SAME url can be served from
  // that opaque entry and taint the canvas. We can't cache-bust a *presigned* URL either (an extra query param breaks the
  // signature). So fetch it explicitly with `mode:"cors"` (a cors request is never satisfiable from the opaque entry) +
  // `cache:"reload"`, then draw from a SAME-ORIGIN blob URL — the canvas is never cross-origin, so `toBlob` export is clean.
  // A missing bucket CORS rule fails the fetch → we surface a clear message instead of a broken/tainted export.
  useEffect(() => {
    if (!media.url) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      imgRef.current = img;
      const c = canvasRef.current;
      if (c) {
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
      }
      setImageReady(true);
    };
    img.onerror = () => {
      if (!cancelled) setLoadError(true);
    };
    fetch(media.url, { mode: "cors", cache: "reload" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        img.src = objectUrl;
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
      img.onload = null;
      img.onerror = null;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [media.url]);

  const lw = imgRef.current ? strokeWidthFor(imgRef.current.naturalWidth, imgRef.current.naturalHeight) : 4;
  const fs = imgRef.current ? fontSizeFor(imgRef.current.naturalWidth, imgRef.current.naturalHeight) : 24;

  let cursorClass = "cursor-crosshair";
  if (tool === "select") cursorClass = "cursor-move";
  else if (tool === "text" || tool === "callout") cursorClass = "cursor-text";

  // `chrome:false` omits the editor-only selection ring — used right before the export snapshot so it never burns into the PNG.
  const redraw = useCallback(
    (opts?: { chrome?: boolean }) => {
      const chrome = opts?.chrome ?? true;
      const c = canvasRef.current;
      const img = imgRef.current;
      if (!c || !img) return;
      const ctx = c.getContext("2d");
      if (!ctx) return;
      ctx.clearRect(0, 0, c.width, c.height);
      ctx.drawImage(img, 0, 0, c.width, c.height);
      const mv = moveDrag.current;
      for (const a of shapes) {
        if (mv?.moved && a.id === mv.id) continue; // the shape being moved is drawn translated below (live preview)
        drawAnnotation(ctx, a, lw, fs);
      }
      if (mv?.moved) {
        ctx.save();
        ctx.translate(mv.dx, mv.dy);
        drawAnnotation(ctx, mv.original, lw, fs);
        ctx.restore();
      }
      if (draft.current) drawAnnotation(ctx, draft.current, lw, fs);
      // Selection ring: editor chrome only (never exported), and hidden mid-drag (the moving shape is its own preview).
      if (chrome && selectedId && !mv?.moved) {
        const sel = shapes.find((s) => s.id === selectedId);
        if (sel) drawSelectionRing(ctx, sel, lw);
      }
    },
    [shapes, lw, fs, selectedId],
  );

  useEffect(() => {
    if (imageReady) redraw();
  }, [imageReady, redraw]);

  // Focus the text/callout input EXPLICITLY after it mounts. `autoFocus` was unreliable: the same pointer-down that opens the
  // input triggers the browser's native mousedown-focus, which lands on the Dialog BEFORE React's autoFocus runs → an instant
  // blur. A `useLayoutEffect` fires after the DOM commit (once that native focus has settled) so this focus wins the race.
  useLayoutEffect(() => {
    if (textInput) textAreaRef.current?.focus({ preventScroll: true });
  }, [textInput]);

  // Selection is only meaningful in the Select tool — leaving it drops the ring.
  useEffect(() => {
    if (tool !== "select") setSelectedId(null);
  }, [tool]);

  // If undo/redo/clear removed the selected shape, drop the dangling selection so the ring can't point at nothing.
  useEffect(() => {
    if (selectedId && !shapes.some((s) => s.id === selectedId)) setSelectedId(null);
  }, [shapes, selectedId]);

  /** Map a pointer event to the image's NATURAL coordinates (the canvas is displayed scaled). */
  const toImage = (e: ReactPointerEvent): { x: number; y: number } => {
    const c = canvasRef.current!;
    const rect = c.getBoundingClientRect();
    return { x: ((e.clientX - rect.left) / rect.width) * c.width, y: ((e.clientY - rect.top) / rect.height) * c.height };
  };

  // One history entry: `before` is snapshotted explicitly (not closed-over `shapes`) so a move — which captures its baseline at
  // pointer-DOWN — records the right undo state regardless of any renders between grab and drop.
  const commit = (before: Annotation[], next: Annotation[]) => {
    setPast((p) => [...p, before]);
    setFuture([]);
    setShapes(next);
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!imageReady || textInput || e.button !== 0) return; // primary button only
    const p = toImage(e);
    if (tool === "select") {
      const c = e.currentTarget;
      const rect = c.getBoundingClientRect();
      const scale = rect.width ? c.width / rect.width : 1; // CSS px → natural px, so the grab tolerance matches the cursor
      const ctx = c.getContext("2d");
      const hit = ctx ? hitTest(shapes, p, ctx, HIT_SLOP_CSS * scale) : null;
      if (hit) {
        setSelectedId(hit.id);
        c.focus(); // so Delete/Backspace + Escape reach the canvas keydown handler
        c.setPointerCapture(e.pointerId);
        moveDrag.current = { id: hit.id, original: hit, before: shapes, startImgX: p.x, startImgY: p.y, startClientX: e.clientX, startClientY: e.clientY, dx: 0, dy: 0, moved: false, pointerId: e.pointerId };
      } else {
        setSelectedId(null); // clicked empty canvas
      }
      return;
    }
    if (tool === "text" || tool === "callout") {
      setTextValue("");
      setTextInput({ screenX: e.clientX, screenY: e.clientY, imgX: p.x, imgY: p.y, kind: tool });
      return;
    }
    e.currentTarget.setPointerCapture(e.pointerId);
    drawing.current = true;
    if (tool === "freehand") draft.current = { id: newId(), type: "freehand", color, points: [p] };
    else if (tool === "arrow") draft.current = { id: newId(), type: "arrow", color, x1: p.x, y1: p.y, x2: p.x, y2: p.y };
    else {
      anchor.current = p; // fixed corner; the box grows from here regardless of drag direction
      draft.current = { id: newId(), type: tool, color, x: p.x, y: p.y, w: 0, h: 0, fill };
    }
    redraw();
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const mv = moveDrag.current;
    if (mv && mv.pointerId === e.pointerId) {
      // Ignore sub-threshold jitter so a select-click doesn't become a 0px move; once moving, track the pointer as a delta.
      const dist = Math.hypot(e.clientX - mv.startClientX, e.clientY - mv.startClientY);
      if (mv.moved || dist >= DRAG_THRESHOLD_CSS) {
        const p = toImage(e);
        mv.moved = true;
        mv.dx = p.x - mv.startImgX;
        mv.dy = p.y - mv.startImgY;
        redraw();
      }
      return;
    }
    if (!drawing.current || !draft.current) return;
    const p = toImage(e);
    const d = draft.current;
    if (d.type === "freehand") d.points.push(p);
    else if (d.type === "arrow") {
      d.x2 = p.x;
      d.y2 = p.y;
    } else if (d.type === "rect" || d.type === "ellipse") {
      const s = anchor.current ?? { x: d.x, y: d.y };
      // Always normalize from the FIXED anchor, never the (mutating) draft box, so left/up drags keep their start corner.
      Object.assign(d, normRect(s.x, s.y, p.x, p.y));
    }
    redraw();
  };
  const onPointerUp = () => {
    // A move gesture takes priority: commit ONE translated shape (one undo entry) if it actually moved, else it was a plain
    // select-click (nothing to commit — the ring is already shown).
    const mv = moveDrag.current;
    if (mv) {
      moveDrag.current = null;
      if (mv.moved) commit(mv.before, mv.before.map((s) => (s.id === mv.id ? translateAnnotation(mv.original, mv.dx, mv.dy) : s)));
      else redraw();
      return;
    }
    if (!drawing.current) return;
    drawing.current = false;
    const d = draft.current;
    draft.current = null;
    if (!d) return;
    // Drop a degenerate (zero-size / single-point) shape.
    const tiny =
      (d.type === "freehand" && d.points.length < 2) ||
      (d.type === "arrow" && Math.hypot(d.x2 - d.x1, d.y2 - d.y1) < 4) ||
      ((d.type === "rect" || d.type === "ellipse") && (d.w < 4 || d.h < 4));
    if (tiny) {
      redraw();
      return;
    }
    commit(shapes, [...shapes, d]);
  };

  // Abandon an in-flight gesture (OS gesture, focus loss) WITHOUT committing — the shape snaps back to its pre-drag position.
  const onPointerCancel = () => {
    if (moveDrag.current) {
      moveDrag.current = null;
      redraw();
    } else if (drawing.current) {
      drawing.current = false;
      draft.current = null;
      redraw();
    }
  };

  // With a shape selected, Delete/Backspace removes it and Escape deselects. Escape must `stopPropagation` or react-aria would
  // read it as "dismiss the dialog" and close the whole editor. Never fires while the text/callout textarea owns the keyboard.
  const onCanvasKeyDown = (e: ReactKeyboardEvent<HTMLCanvasElement>) => {
    if (textInput || !selectedId) return;
    if (e.key === "Delete" || e.key === "Backspace") {
      e.preventDefault();
      commit(shapes, shapes.filter((s) => s.id !== selectedId));
      setSelectedId(null);
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      setSelectedId(null);
    }
  };

  const commitText = () => {
    if (!textInput) return;
    const t = textValue.trim();
    if (t) {
      const ctx = canvasRef.current?.getContext("2d");
      if (textInput.kind === "text") {
        commit(shapes, [...shapes, { id: newId(), type: "text", color, x: textInput.imgX, y: textInput.imgY, text: t, fontSize: fs }]);
      } else {
        // Size the callout box to the text.
        if (ctx) ctx.font = `600 ${fs}px Helvetica, Arial, sans-serif`;
        const lines = t.split("\n");
        const wText = ctx ? Math.max(...lines.map((l) => ctx.measureText(l).width)) : fs * 8;
        const pad = Math.max(6, fs / 3);
        commit(shapes, [
          ...shapes,
          { id: newId(), type: "callout", color, x: textInput.imgX, y: textInput.imgY, w: wText + pad * 2, h: lines.length * fs * 1.25 + pad * 2, text: t, fontSize: fs },
        ]);
      }
    }
    setTextInput(null);
    setTextValue("");
  };

  const undo = () => {
    if (past.length === 0) return;
    setFuture((f) => [shapes, ...f]);
    setShapes(past[past.length - 1]!);
    setPast((p) => p.slice(0, -1));
  };
  const redo = () => {
    if (future.length === 0) return;
    setPast((p) => [...p, shapes]);
    setShapes(future[0]!);
    setFuture((f) => f.slice(1));
  };
  const clearAll = () => {
    if (shapes.length > 0) commit(shapes, []);
  };

  const save = () => {
    const c = canvasRef.current;
    if (!c) return;
    draft.current = null;
    moveDrag.current = null;
    redraw({ chrome: false }); // flush the selection ring off the canvas BEFORE the snapshot so it never enters the PNG
    setSaving(true);
    setError(null);
    // Bound the export resolution so a large source can't blow the upload byte cap / the server's decode-pixel limit.
    boundedForExport(c).toBlob((blob) => {
      if (!blob) {
        setSaving(false);
        setError(t("manualEditor.renderFailed"));
        return;
      }
      const base = (media.name ?? "image").replace(/\.[^./\\]+$/, "");
      const file = new File([blob], `${base}_edited.png`, { type: "image/png" });
      // Hand the PNG to the caller (the A3a edit-attach write) — on success signal `onSaved` (the caller closes the editor
      // and shows the edited image in the lightbox); on error keep the editor open with the message so the author can retry.
      onSave(file)
        .then(() => onSaved())
        .catch((e: unknown) => setError(e instanceof Error ? e.message : t("manualEditor.saveFailed")))
        .finally(() => setSaving(false));
    }, "image/png");
  };

  return (
    <ModalOverlay isDismissable isOpen onOpenChange={(o) => !o && onClose()} className="fixed inset-0 z-[70] flex flex-col bg-black/80 p-4">
      <Modal className="flex min-h-0 w-full flex-1 flex-col outline-hidden">
        <Dialog aria-label={t("manualEditor.editImageOf", { name: media.name ?? t("manualEditor.imageFallback") })} className="flex min-h-0 flex-1 flex-col outline-none">
          {/* Header */}
          <div className="mb-3 flex items-center justify-between gap-3">
            <span className="truncate text-sm font-medium text-white">{t("manualEditor.editImageHeadingFull", { name: media.name ?? t("manualEditor.imageFallback") })}</span>
            <Button variant="ghost" size="icon-lg" aria-label={t("manualEditor.closeEditor")} onPress={onClose} className="text-white hover:bg-white/15 hover:text-white">
              <X aria-hidden="true" />
            </Button>
          </div>

          {/* Toolbar */}
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg bg-white/10 p-2">
            <div className="flex items-center gap-1" role="group" aria-label={t("manualEditor.tools")}>
              {TOOLS.map(({ tool: toolId, labelKey, icon: Icon }) => (
                <TooltipTrigger key={toolId} delay={0}>
                  <Button
                    variant={tool === toolId ? "default" : "ghost"}
                    size="icon-sm"
                    aria-label={t(labelKey)}
                    aria-pressed={tool === toolId}
                    onPress={() => setTool(toolId)}
                    className={cn(tool !== toolId && "text-white hover:bg-white/15 hover:text-white")}
                  >
                    <Icon aria-hidden="true" className="size-4" />
                  </Button>
                  <Tooltip>{t(labelKey)}</Tooltip>
                </TooltipTrigger>
              ))}
            </div>
            <span className="mx-1 h-6 w-px bg-white/25" aria-hidden="true" />
            <div className="flex items-center gap-1.5" role="group" aria-label={t("manualEditor.colour")}>
              {PALETTE.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label={t("manualEditor.colourN", { c })}
                  aria-pressed={color === c}
                  onClick={() => setColor(c)}
                  className={cn("size-6 rounded-full border-2", color === c ? "border-white ring-2 ring-white/60" : "border-white/40")}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
            <span className="mx-1 h-6 w-px bg-white/25" aria-hidden="true" />
            {/* Fill toggle — a NEW rectangle/ellipse gets a translucent colour fill instead of an outline only. */}
            <TooltipTrigger delay={0}>
              <Button
                variant={fill ? "default" : "ghost"}
                size="icon-sm"
                aria-label={t("manualEditor.fillShapes")}
                aria-pressed={fill}
                onPress={() => setFill((f) => !f)}
                className={cn(!fill && "text-white hover:bg-white/15 hover:text-white")}
              >
                <PaintBucket aria-hidden="true" className="size-4" />
              </Button>
              <Tooltip>{t("manualEditor.fillRectEllipse")}</Tooltip>
            </TooltipTrigger>
            <span className="mx-1 h-6 w-px bg-white/25" aria-hidden="true" />
            <Button variant="ghost" size="icon-sm" aria-label={t("manualEditor.undo")} isDisabled={past.length === 0} onPress={undo} className="text-white hover:bg-white/15 hover:text-white disabled:opacity-40">
              <Undo2 aria-hidden="true" className="size-4" />
            </Button>
            <Button variant="ghost" size="icon-sm" aria-label={t("manualEditor.redo")} isDisabled={future.length === 0} onPress={redo} className="text-white hover:bg-white/15 hover:text-white disabled:opacity-40">
              <Redo2 aria-hidden="true" className="size-4" />
            </Button>
            <Button variant="ghost" size="icon-sm" aria-label={t("manualEditor.clearAll")} isDisabled={shapes.length === 0} onPress={clearAll} className="text-white hover:bg-white/15 hover:text-white disabled:opacity-40">
              <Trash2 aria-hidden="true" className="size-4" />
            </Button>
            <div className="ml-auto flex items-center gap-2">
              <Button variant="ghost" size="sm" onPress={onClose} className="text-white hover:bg-white/15 hover:text-white">
                {t("manualEditor.cancel")}
              </Button>
              <Button variant="default" size="sm" onPress={save} isDisabled={saving || !imageReady || loadError}>
                {saving ? <Loader2 aria-hidden="true" className="mr-1.5 size-3.5 animate-spin" /> : null}
                {saving ? t("manualEditor.saving") : t("manualEditor.saveAsNewImage")}
              </Button>
            </div>
          </div>

          {/* Status line */}
          {error ? (
            <p role="alert" className="mb-2 rounded-md bg-red-500/20 px-3 py-1.5 text-xs text-red-100">
              {error}
            </p>
          ) : null}

          {/* Canvas stage */}
          <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden">
            {loadError ? (
              <p className="max-w-md text-center text-sm text-white/80">
                {t("manualEditor.imageLoadCorsFailed")}
              </p>
            ) : (
              /* Shape PLACEMENT is pointer-native here (like the legacy MarkerJS2 surface); the toolbar (tools/colour/
                 undo/redo/save) is fully keyboard-operable, and with a shape selected Delete/Backspace + Escape work, but
                 there is no keyboard path to *draw* or *select* a shape yet — a dedicated keyboard placement/nudge flow +
                 live-region is a tracked follow-up (panel A3a #3). `tabIndex` makes the canvas focusable so the selected-shape
                 keys land here rather than bubbling to the dialog. */
              <canvas
                ref={canvasRef}
                tabIndex={0}
                aria-label={t("manualEditor.annotateCanvas", { name: media.name ?? t("manualEditor.imageFallback") })}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerCancel}
                onKeyDown={onCanvasKeyDown}
                className={cn("max-h-full max-w-full touch-none bg-white/5 outline-none", cursorClass)}
              />
            )}
            {!imageReady && !loadError ? <Loader2 aria-hidden="true" className="absolute size-8 animate-spin text-white/70" /> : null}
          </div>

          {/* Text / callout input overlay — rendered INSIDE the Dialog (NOT portalled to document.body: react-aria's modal
              `ariaHideOutside` inert-marks any node added to body outside the modal). Focus is set in a `useLayoutEffect`
              (see above), NOT `autoFocus`. There is deliberately NO `onBlur`-commit: `commitText` always clears the input, so
              a generic blur (the native mousedown-focus, or clicking a toolbar button) would SILENTLY cancel it — the actual
              bug behind "text/callout not working" (Codex-diagnosed). Commit/cancel are explicit via Enter/Escape only, and
              both `stopPropagation` so Escape can't ALSO bubble to the Dialog and close the whole editor (react-aria dismisses
              the topmost overlay on Escape regardless of `isDismissable`). `position: fixed` floats it at the click point,
              clamped against the viewport so it never spills off the right/bottom edge. */}
          {textInput ? (
            <div
              className="fixed z-[80]"
              style={{
                left: Math.max(8, Math.min(textInput.screenX, window.innerWidth - 184)),
                top: Math.max(8, Math.min(textInput.screenY, window.innerHeight - 88)),
              }}
            >
              <textarea
                ref={textAreaRef}
                value={textValue}
                onChange={(e) => setTextValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    e.preventDefault();
                    e.stopPropagation(); // don't let react-aria dismiss the whole editor
                    setTextInput(null);
                    setTextValue("");
                  } else if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    e.stopPropagation();
                    commitText();
                  }
                }}
                rows={2}
                placeholder={t("manualEditor.typeThenEnter")}
                className="border-border bg-background text-foreground min-w-40 rounded-md border p-1.5 text-sm shadow-xl outline-none"
              />
            </div>
          ) : null}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
